import client from './client'

export interface TaggedAlumnus {
  id: string
  name: string
  avatar_url: string
  company: string
  job_title: string
  is_verified: number
}

export interface QuestionSummary {
  id: string
  user_id: string
  title: string
  description: string
  category: string
  tags: string[]
  status: 'answered' | 'unanswered' | 'closed'
  answers_count: number
  college_context?: string
  course_context?: string
  created_at: string
  updated_at?: string
  student_name: string
  student_username: string
  student_avatar: string
  student_college?: string
  student_course?: string
  tagged_alumni?: TaggedAlumnus[]
  most_helpful_answer_preview?: {
    id: string
    content: string
    helpful_count: number
    author_name: string
    author_avatar: string
    author_title: string
    author_company: string
    is_verified?: number
  } | null
  is_saved?: boolean
}

export interface AnswerReply {
  id: string
  answer_id: string
  author_id: string
  content: string
  created_at: string
  author_name: string
  author_username: string
  author_avatar: string
  is_alumnus?: boolean
}

export interface QuestionAnswer {
  id: string
  question_id: string
  author_id: string
  content: string
  helpful_count: number
  is_accepted?: boolean
  created_at: string
  updated_at?: string
  author_name: string
  author_username: string
  author_avatar: string
  author_title: string
  author_company: string
  author_grad_year?: number
  author_degree?: string
  author_college?: string
  is_verified?: number
  has_voted?: boolean
  is_saved?: boolean
  replies?: AnswerReply[]
}

export interface QuestionDetail extends QuestionSummary {
  answers: QuestionAnswer[]
  is_author?: boolean
}

export interface QuestionFilterParams {
  q?: string
  category?: string
  tag?: string
  sort?: 'newest' | 'most_answered' | 'unanswered'
  alumnus_id?: string
  user_id?: string
  status?: string
  page?: number
  limit?: number
}

export interface CreateQuestionPayload {
  title: string
  description: string
  category: string
  tags: string[]
  target_alumni_ids?: string[]
  college_context?: string
  course_context?: string
}

export interface EditQuestionPayload {
  title?: string
  description?: string
  category?: string
  tags?: string[]
  target_alumni_ids?: string[]
}

export interface CreateAnswerPayload {
  content: string
}

export interface CreateReplyPayload {
  content: string
}

export interface ReportPayload {
  target_type: 'question' | 'answer'
  target_id: string
  reason: string
  details?: string
}

export interface SavedQAData {
  questions: QuestionSummary[]
  answers: (QuestionAnswer & {
    question_title: string
    question_category: string
  })[]
}

export const QA_CATEGORIES = [
  'Placements and Interviews',
  'Software Engineering and Technology',
  'Internships',
  'Career Guidance',
  'Resume and Portfolio',
  'Higher Education',
  'Competitive Exams',
  'Entrepreneurship and Startups',
  'Design and Product Management',
  'Finance and Business',
  'Communication and Professional Skills',
  'Campus Life and Career Preparation',
] as const

export type QACategory = (typeof QA_CATEGORIES)[number]

export const qaApi = {
  getQuestions: (params?: QuestionFilterParams) =>
    client.get<{ success: boolean; data: QuestionSummary[]; meta?: { count: number; page: number } }>('/qa/questions', { params }),

  getQuestion: (id: string) =>
    client.get<{ success: boolean; data: QuestionDetail }>(`/qa/questions/${encodeURIComponent(id)}`),

  createQuestion: (data: CreateQuestionPayload) =>
    client.post<{ success: boolean; data: QuestionDetail }>('/qa/questions', data),

  editQuestion: (id: string, data: EditQuestionPayload) =>
    client.put<{ success: boolean; data: QuestionDetail }>(`/qa/questions/${encodeURIComponent(id)}`, data),

  deleteQuestion: (id: string) =>
    client.delete<{ success: boolean }>(`/qa/questions/${encodeURIComponent(id)}`),

  createAnswer: (questionId: string, data: CreateAnswerPayload) =>
    client.post<{ success: boolean; data: QuestionAnswer }>(`/qa/questions/${encodeURIComponent(questionId)}/answers`, data),

  editAnswer: (answerId: string, data: CreateAnswerPayload) =>
    client.put<{ success: boolean; data: QuestionAnswer }>(`/qa/answers/${encodeURIComponent(answerId)}`, data),

  deleteAnswer: (answerId: string) =>
    client.delete<{ success: boolean }>(`/qa/answers/${encodeURIComponent(answerId)}`),

  voteAnswer: (answerId: string) =>
    client.post<{ success: boolean; voted: boolean; helpful_count: number }>(`/qa/answers/${encodeURIComponent(answerId)}/vote`),

  replyAnswer: (answerId: string, data: CreateReplyPayload) =>
    client.post<{ success: boolean; data: AnswerReply }>(`/qa/answers/${encodeURIComponent(answerId)}/replies`, data),

  getSaved: (type: 'all' | 'question' | 'answer' = 'all') =>
    client.get<{ success: boolean; data: SavedQAData }>('/qa/saved', { params: { type } }),

  toggleSave: (itemType: 'question' | 'answer', itemId: string) =>
    client.post<{ success: boolean; is_saved: boolean }>('/qa/saved', { item_type: itemType, item_id: itemId }),

  report: (data: ReportPayload) =>
    client.post<{ success: boolean }>('/qa/report', data),

  getCategories: () =>
    client.get<{ success: boolean; data: string[] }>('/qa/categories'),
}
